import { addDays, differenceInCalendarDays, format, getDay, isValid, parseISO, startOfWeek } from "date-fns";
import * as z from "zod";
import { weekdayKeys } from "../schedule-time/schedule-dates";
import { SlotSchema } from "./slot-fields";

export { SlotSchema } from "./slot-fields";

const DateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((value) => {
    const date = parseISO(value);
    return isValid(date) && format(date, "yyyy-MM-dd") === value;
  }, "Invalid date");

export const OfflineMessagesSchema = z
  .record(DateSchema, z.string())
  .describe("Message shown on that day when it has no stream.");
export type OfflineMessages = z.infer<typeof OfflineMessagesSchema>;

export const OfflineActiveSchema = z.record(DateSchema, z.boolean()).describe("true shows that day's offline message.");
export type OfflineActive = z.infer<typeof OfflineActiveSchema>;

export type Slot = z.infer<typeof SlotSchema>;

const CompactSlotKeySchema = z.string().regex(/^(mon|tue|wed|thu|fri|sat|sun)-[12]$/);

// Chat apps usually wrap the JSON in a code fence and often add a sentence around it.
function extractJson(text: string) {
  const fence = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fence) return fence[1]!.trim();
  const firstBrace = text.indexOf("{");
  const lastBrace = text.lastIndexOf("}");
  const hasObject = firstBrace !== -1 && lastBrace > firstBrace;
  return hasObject ? text.slice(firstBrace, lastBrace + 1) : text.trim();
}

export const ScheduleDocumentSchema = z.object({
  version: z.literal(1),
  selectedDate: DateSchema.refine((value) => getDay(parseISO(value)) === 1, "Schedule date must be a Monday").describe(
    "Must be a Monday.",
  ),
  timezone: z
    .string()
    .refine((value) => {
      try {
        new Intl.DateTimeFormat("en", { timeZone: value });
        return true;
      } catch {
        return false;
      }
    }, "Invalid timezone")
    .describe("IANA timezone name."),
  slots: z.record(CompactSlotKeySchema, SlotSchema),
  commonOptions: z
    .object({ memberOnlyLabel: z.string().optional().describe("Defaults to 'Member' for enUS and '會限' for zhTW.") })
    .default({}),
  offlineMessages: OfflineMessagesSchema.default({}),
  offlineActive: OfflineActiveSchema.default({}),
});
export type ScheduleDocument = z.infer<typeof ScheduleDocumentSchema>;

function compactSlotKey(date: string, number: string, monday: Date) {
  const day = differenceInCalendarDays(parseISO(date), monday);
  return day >= 0 && day < weekdayKeys.length ? `${weekdayKeys[day]}-${number}` : null;
}

function compactSlots(slots: Record<string, Slot>, monday: Date) {
  const compactEntries = Object.entries(slots).flatMap(([key, slot]) => {
    const [date, number] = key.split("-").length === 4 ? [key.slice(0, 10), key.slice(11)] : key.split("-");
    const compactKey = date && number && date.length === 10 ? compactSlotKey(date, number, monday) : key;
    return compactKey ? [[compactKey, slot] as const] : [];
  });
  return Object.fromEntries(compactEntries);
}

export function expandScheduleSlots(slots: Record<string, Slot>, monday: Date) {
  return Object.fromEntries(
    Object.entries(slots).map(([key, slot]) => {
      const [weekday, number] = key.split("-");
      const day = weekdayKeys.indexOf(weekday as (typeof weekdayKeys)[number]);
      const date = format(addDays(monday, day), "yyyy-MM-dd");
      return [`${date}-${number}`, slot];
    }),
  );
}

export function scheduleDocument(state: {
  commonOptions: ScheduleDocument["commonOptions"];
  offlineMessages: OfflineMessages;
  offlineActive: OfflineActive;
  selectedDate: Date;
  timezone: string;
  slots: Record<string, Slot>;
}): ScheduleDocument {
  const monday = startOfWeek(state.selectedDate, { weekStartsOn: 1 });
  return {
    version: 1,
    selectedDate: format(monday, "yyyy-MM-dd"),
    timezone: state.timezone,
    slots: compactSlots(state.slots, monday),
    commonOptions: state.commonOptions,
    offlineMessages: state.offlineMessages,
    offlineActive: state.offlineActive,
  };
}

export function parseScheduleDocument(json: string): ScheduleDocument {
  return ScheduleDocumentSchema.parse(JSON.parse(extractJson(json)));
}
